import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Award, DoorOpen, Users, UserCheck, UserX, ShieldAlert, TrendingUp } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "@/lib/api";

const StatCard = ({ testId, label, value, suffix, icon: Icon, tone }) => (
  <div className="bg-white rounded-2xl border p-5 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${tone}`}>
      <Icon className="w-6 h-6" />
    </div>
    <div>
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className="text-2xl font-extrabold text-slate-900" data-testid={testId}>
        {value} <span className="text-sm font-medium text-slate-400">{suffix}</span>
      </p>
    </div>
  </div>
);

const RankList = ({ testId, title, icon: Icon, iconTone, badgeTone, items, badge, subline }) => (
  <div className="bg-white rounded-2xl border p-5 shadow-sm">
    <div className="flex items-center gap-2 mb-4">
      <Icon className={`w-5 h-5 ${iconTone}`} />
      <h3 className="font-bold">{title}</h3>
    </div>
    <div className="space-y-3" data-testid={testId}>
      {items.length === 0 && <p className="text-sm text-slate-400">لا توجد بيانات بعد</p>}
      {items.map((e, i) => (
        <div key={e.employee_name} className="flex items-center justify-between gap-2 border-b last:border-0 pb-2.5">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-6 h-6 rounded-full bg-emerald-900 text-white text-xs flex items-center justify-center shrink-0">
              {i + 1}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{e.employee_name}</p>
              <p className="text-[11px] text-slate-400">{subline(e)}</p>
            </div>
          </div>
          <span className={`text-xs font-bold rounded-full px-2 py-0.5 border shrink-0 ${badgeTone}`}>
            {badge(e)}
          </span>
        </div>
      ))}
    </div>
  </div>
);

export default function OverviewTab() {
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState("all");
  const [boards, setBoards] = useState(null);

  useEffect(() => {
    api.get("/reports/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (period === "all") { setBoards(null); return; }
    const frm = dayjs().startOf("month").format("YYYY-MM-DD");
    const to = dayjs().endOf("month").format("YYYY-MM-DD");
    api.get("/reports/summary", { params: { frm, to } }).then((r) => setBoards(r.data)).catch(() => {});
  }, [period]);

  if (!summary)
    return <div className="py-20 text-center text-slate-400" data-testid="overview-loading">جارٍ تحميل المؤشرات...</div>;

  const boardData = (period === "month" && boards) ? boards : summary;

  return (
    <div className="space-y-6" data-testid="overview-tab">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <StatCard testId="stat-total-employees" label="إجمالي الموظفين" value={summary.total_employees}
          suffix="موظف" icon={Users} tone="bg-emerald-50 text-emerald-700" />
        <StatCard testId="stat-attendance-rate" label="نسبة الحضور" value={summary.attendance_rate}
          suffix="%" icon={UserCheck} tone="bg-blue-50 text-blue-700" />
        <StatCard testId="stat-accountability-cases" label="حالات المساءلة" value={summary.accountability_cases}
          suffix="حالة" icon={ShieldAlert} tone="bg-rose-50 text-rose-700" />
      </div>

      <div className="flex items-center justify-end gap-2" data-testid="boards-period-bar">
        <span className="text-xs text-slate-400">لوحات الأكثر:</span>
        {[["all", "كل الفترات"], ["month", "هذا الشهر"]].map(([v, label]) => (
          <button key={v} type="button" onClick={() => setPeriod(v)}
            data-testid={`boards-period-${v}`}
            className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${
              period === v
                ? "bg-emerald-900 text-white border-emerald-900"
                : "text-slate-500 border-slate-200 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 bg-white rounded-2xl border p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-emerald-800" />
            <h3 className="font-bold">اتجاه دقائق التأخير اليومي</h3>
          </div>
          <div className="h-72" data-testid="chart-daily-lateness">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={summary.daily} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => [`${v} دقيقة`, "التأخير"]} />
                <Bar dataKey="late_minutes" fill="#064E3B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <h3 className="font-bold mb-4">الأكثر تأخراً (أعلى 5)</h3>
          <div className="space-y-3" data-testid="top-late-list">
            {boardData.top_late.length === 0 && <p className="text-sm text-slate-400">لا توجد بيانات بعد</p>}
            {boardData.top_late.map((e, i) => (
              <div key={e.employee_name} className="flex items-center justify-between gap-2 border-b last:border-0 pb-2.5">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="w-6 h-6 rounded-full bg-emerald-900 text-white text-xs flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{e.employee_name}</p>
                    <p className="text-[11px] text-slate-400">{e.department || "—"}</p>
                  </div>
                </div>
                <span className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 shrink-0">
                  {e.total_late_minutes} د
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <RankList testId="top-early-list" title="الأكثر انصرافاً مبكراً (أعلى 5)" icon={DoorOpen}
          iconTone="text-rose-700" badgeTone="text-rose-700 bg-rose-50 border-rose-200"
          items={boardData.top_early}
          badge={(e) => `${e.total_early_minutes} د`}
          subline={(e) => e.department || "—"} />
        <RankList testId="top-absent-list" title="الأكثر غياباً (أعلى 5)" icon={UserX}
          iconTone="text-slate-600" badgeTone="text-slate-700 bg-slate-100 border-slate-300"
          items={boardData.top_absent}
          badge={(e) => `${e.absent_days} أيام`}
          subline={(e) => e.department || "—"} />
        <RankList testId="most-disciplined-list" title="الأكثر انضباطاً (أقل 5)" icon={Award}
          iconTone="text-emerald-700" badgeTone="text-emerald-800 bg-emerald-50 border-emerald-200"
          items={boardData.most_disciplined}
          badge={(e) => `${e.total_late_minutes + (e.total_early_minutes || 0) + (e.absent_days || 0)} نقطة`}
          subline={(e) => `تأخير ${e.total_late_minutes} د · خروج ${e.total_early_minutes || 0} د · غياب ${e.absent_days || 0}`} />
      </div>
    </div>
  );
}
